import { useState } from "react";
import { ConversationAvatar } from "../../conversation-avatar";
import {
  BlockContainer,
  ConversationAvatarWrapper,
  MessageContainer,
  MessageHiddenNote,
  RootContainer,
} from "./styled";
import { renderContent } from "./message-types/message-content-render";
import { ServerMessageInfoProps } from "./types";
import { ReplyToBlock } from "./reply-to-block";
import { ReactionsChangeData } from "../components/message-reactions";
import { useFormatMessageDateTime } from "../components/date-label";
import { SMUAccountSummary } from "@inbox/entities/smu-accounts";
import { SentimentBadge } from "./sentiment-badge";
import { useSelector } from "react-redux";
import {
  getConversationAccountContext,
  WrappedInboxState,
} from "selectors/conversationInboxSelectors";
import { cn } from "@/lib/utils";
import { MessageGroupingProps } from "../components/messages-layout/messages-layout-common";
import { UnknownUserInfoIcon } from "@inbox/shared/common-styles";

interface SMUMessageProps extends ServerMessageInfoProps {
  conversationId: string;
  interlocutorId: string;
  onReactionsChange: (data: ReactionsChangeData) => void;
  opaque?: boolean;
  selected?: boolean;
  onSelected?: (id: string) => void;
  messageGrouping?: MessageGroupingProps;
}

export function SMUMessage({
  src,
  message,
  timestamp,
  name,
  type,
  story,
  attachments,
  replyTo,
  interlocutorId,
  reactions,
  messageId,
  onReactionsChange,
  attachment,
  originalMessage,
  conversationId,
  opaque,
  selected,
  onSelected,
  conversation,
  messageGrouping,
}: SMUMessageProps): JSX.Element {
  const [showAccountSummary, setShowAccountSummary] = useState<boolean>(false);
  const date = useFormatMessageDateTime(timestamp);
  const convAccount = useSelector((state: WrappedInboxState) =>
    getConversationAccountContext(state, conversationId)
  );

  return (
    <RootContainer left isFirstInGroup={messageGrouping?.isFirstInGroup}>
      {messageGrouping?.isFirstInGroup && (
        <div className="flex items-center pl-[50px] pb-0.5">
          <span className="text-xs font-medium text-ink-secondary">{name}</span>
          {originalMessage?.sender?.isUnknownSource && (
            <UnknownUserInfoIcon className="ml-1" />
          )}
        </div>
      )}
      <MessageContainer left>
        <ConversationAvatarWrapper
          className={cn(!messageGrouping?.isFirstInGroup && "invisible")}
          onMouseEnter={() => setShowAccountSummary(true)}
          onMouseLeave={() => setShowAccountSummary(false)}
        >
          <ConversationAvatar
            src={src}
            name={name}
            unknownUser={originalMessage?.sender?.isUnknownSource}
          />
          <SMUAccountSummary
            top={82}
            left={1}
            visible={showAccountSummary}
            conversationId={conversationId}
            interlocutor={originalMessage?.sender}
          />
        </ConversationAvatarWrapper>
        <BlockContainer
          opaque={
            !!originalMessage?.deleted || !!originalMessage?.hidden || opaque
          }
        >
          {replyTo && (
            <ReplyToBlock
              message={replyTo.message}
              name={replyTo.name}
              type={replyTo.type}
              story={replyTo.story}
              attachments={replyTo.attachments}
              isLeft
              isCurrentSMUMessage
              isReplyToSMUMessage={replyTo.sender?.id == interlocutorId}
              messageId={replyTo.messageId}
              originalMessage={replyTo.originalMessage}
              conversationId={conversationId}
            />
          )}
          {renderContent({
            type,
            name,
            story,
            attachments,
            reactions,
            onReactionsChange,
            message,
            isLeft: true,
            isRepliedMessage: false,
            messageId,
            attachment,
            originalMessage,
            conversationId,
            selected,
            onSelected,
            conversation,
            accountContext: convAccount,
          })}
        </BlockContainer>
      </MessageContainer>
      {messageGrouping?.isLastInGroup && (
        <div className="flex items-center gap-1.5 pl-[50px] pt-1">
          <span className="text-xs text-ink-muted">{date}</span>
          <SentimentBadge sentiment={originalMessage?.sentiment} />
        </div>
      )}
      {originalMessage?.hidden && (
        <MessageHiddenNote>Message has been hidden</MessageHiddenNote>
      )}
    </RootContainer>
  );
}
